//@ts-nocheck
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createFieldNote, deleteFieldNote } from '@/api/fieldNotes';
import { useAuth } from '@/providers/AuthProvider';
import { handleAPIError } from '@/utils/errorHandler';
import {
  getAssetsLayer,
  getFieldNotesLayer,
  getLayerFeatures,
  getMapLayerConfigs,
  getPremisesLayer,
  getServiceAreasLayer,
  getTicketsLayer,
  updateLayerConfig,
} from '@/api/map-layers';
import { AssetFilters } from '@/api/assets';
import {
  LayerDefinition,
  LayerSchemaConfig,
  LayerStyles,
} from '@packages/database/types';

// =========================================================================
// QUERIES
// =========================================================================

export const useMapLayerConfigs = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['layers', orgId],
    queryFn: () => getMapLayerConfigs(),
    enabled: !!orgId,
    staleTime: Infinity,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useLayerFeatures = (layerId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['features', orgId, layerId],
    queryFn: () => getLayerFeatures(layerId),
    enabled: !!orgId,
    staleTime: Infinity,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

// =========================================================================
// MUTATIONS
// =========================================================================

export const useUpdateLayerConfig = (layerId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id!;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: {
      styles?: LayerStyles;
      schemaConfig?: LayerSchemaConfig;
    }) => updateLayerConfig(layerId, input),

    onSuccess: (updated) => {
      // Update list cache
      queryClient.setQueryData(
        ['layers', orgId],
        (prev: LayerDefinition[] | undefined) =>
          prev?.map((l) => (l.id === layerId ? updated : l)),
      );
      // Invalidate features
      queryClient.invalidateQueries({
        queryKey: ['features', orgId, layerId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateLayerConfig: mutation.mutate,
    updateLayerConfigAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
